import { useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';

import { InfoSvg } from '../../../../shared/Assets/svgIcons';
import { PEARL } from '../../../../shared/Helpers/colors';
import { CustomButton, Picker, Text } from '../../../../shared/uiComponents';
import { Unlocked } from '../../../../shared/Assets/sessionStatuses';
import { dateNormalizer, errorNormalizer } from '../../../../shared/Helpers/functions';
import { useCheckPermission } from '../../../../shared/Helpers/hooks';
import { useResetCalendarData } from '../../Helpers/hooks';
import { PERMISSIONS } from '../../../../App/constants';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { activateSession, unlockSession } from '../../../../redux/State/clientSlice/sessionSlice';
import { EventDataProperties } from '../../../../redux/API/ClientAPIHelpers/calendarProperties';

const initialExpirationDate = dayjs().add(2, 'days');

const UnlockSession = ({ data, onClose }: { data: EventDataProperties | null; onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();
	const { permissionGranted } = useCheckPermission(PERMISSIONS.SOAPNOTE.UNLOCK);

	const [openUnlock, setOpenUnlock] = useState<boolean>(false);
	const [expirationDateValue, setExpirationDateValue] = useState<Dayjs | null>(initialExpirationDate);

	const noteStatus = data?.reportStatus?.status;
	const isNoteExpired = noteStatus === SoapNotesStatuses.expired48 || noteStatus === SoapNotesStatuses.expiredWeek;
	const hasNoNote = noteStatus == null; // The loose equality == null checks for both undefined and null
	const canUnlock = hasNoNote || isNoteExpired;

	const toggleUnlockHandler = () => setOpenUnlock(!openUnlock);

	const unlockSessionHandler = () => {
		if (!data?.id || !expirationDateValue) return;
		const { id } = data;
		dispatch(
			unlockSession({
				eventId: id,
				nextExpirationDate: dateNormalizer(expirationDateValue),
			}),
		)
			.then(unwrapResult)
			.then(() => {
				resetData();
				toast('Unlocked');
				onClose();
			})
			.catch(errorNormalizer);
	};

	// Show only if: (No note OR Expired) AND has permission
	if (!permissionGranted || !canUnlock) {
		return null;
	}

	return (
		<>
			{!openUnlock && (
				<div className="actionRow" onClick={toggleUnlockHandler}>
					<div className="actionIcon">
						<Unlocked color={PEARL.BASE[600]} />
					</div>
					<div className="actionContent">
						<Text title="Unlock" type="psm" fontWeight="medium" />
					</div>
				</div>
			)}
			{openUnlock && (
				<div className="actionRow">
					<Picker.CustomDate
						label=""
						value={expirationDateValue}
						onChange={setExpirationDateValue}
						className="flex100"
					/>
					<CustomButton className="flex100" title="Save" onClick={unlockSessionHandler} />
					<CustomButton buttonType="secondaryGray" title="Cancel" onClick={toggleUnlockHandler} />
				</div>
			)}
		</>
	);
};

const ActivateSession = ({ data, onClose }: { data: EventDataProperties | null; onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();

	const [openActivate, setOpenActivate] = useState<boolean>(false);

	const isSessionCreated = Boolean(data?.sessionId);
	const isNoteSubmitted = data?.reportStatus?.status === SoapNotesStatuses.submitted;

	const toggleActivateHandler = () => setOpenActivate(!openActivate);

	const activateSessionHandler = () => {
		if (!data?.sessionId) return;
		const { sessionId } = data;
		dispatch(activateSession(sessionId))
			.then(unwrapResult)
			.then(() => {
				resetData();
				toast('Activated');
				onClose();
			})
			.catch(errorNormalizer);
	};

	// Show only if: Session exists AND note is submitted
	if (!isSessionCreated || !isNoteSubmitted) return null;

	return (
		<>
			{!openActivate && (
				<div className="actionRow" onClick={toggleActivateHandler}>
					<div className="actionIcon">
						<Unlocked color={PEARL.BASE[600]} />
					</div>
					<div className="actionContent">
						<Text title="Activate" type="psm" fontWeight="medium" />
					</div>
				</div>
			)}
			{openActivate && (
				<div className="actionRow">
					<Text title="Activate this session?" type="psm" fontWeight="medium" />
					<CustomButton className="flex100" title="Activate" onClick={activateSessionHandler} />
					<CustomButton buttonType="secondaryGray" title="Cancel" onClick={toggleActivateHandler} />
				</div>
			)}
		</>
	);
};

const AdminActions = () => {
	const [openActions, setOpenActions] = useState<boolean>(false);

	const data = useSelector((state) => state.calendarHelper.infoEventData);

	const toggleAdminActionsHandler = () => setOpenActions(!openActions);
	const closeActionsHandler = () => setOpenActions(false);

	return (
		<div className="icon">
			<InfoSvg onClick={toggleAdminActionsHandler} />
			<div style={{ display: openActions ? 'flex' : 'none' }} className="adminActions">
				<UnlockSession data={data} onClose={closeActionsHandler} />
				<ActivateSession data={data} onClose={closeActionsHandler} />
			</div>
		</div>
	);
};

export default AdminActions;
